import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of, switchMap } from 'rxjs';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { ciInterface } from 'src/app/util/interfaces/ci';
import { enduserInterface } from 'src/app/util/interfaces/enduser';
import { LocalityInterface } from 'src/app/util/interfaces/locality';
import { stockInterface } from 'src/app/util/interfaces/stock';
import { environment } from 'src/environments/environment';
import { CmdbRegisterPageService } from 'src/app/page/cmdb/cmdb-register-page/service/cmdb-register-page.service';
import { ciRelationshipInterface } from 'src/app/util/interfaces/ciRelationship';
import { ShkDate } from 'shk-date';
import { DateService } from 'src/app/services/date/date.service';

@Injectable({
	providedIn: 'root',
})
export class StockPageService {
	private getAvailableUsersUrl = environment.baseApiUrl + 'request/users';
	private getStockLogsUrl = environment.baseApiUrl + 'stock/search?{qryString}';
	private getStockUrl = environment.baseApiUrl + 'stock/search/latest?{qryString}';
	private getPendingLendingUrl = environment.baseApiUrl + 'stock/search/lend?{querystring}';
	private postStockUrl = environment.baseApiUrl + 'stock';
	private getAllLocalitysURL = environment.baseApiUrl + 'location/search';
	private getUserLendingUrl = environment.baseApiUrl + 'stock/user/{idEnduser}';

	headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	constructor(
		private AuthLoginService: AuthLoginService,
		private CmdbRegisterPageService: CmdbRegisterPageService,
		private http: HttpClient,
		private DateService: DateService
	) {}

	// Buscar por todas localidades
	getAllLocations(): Observable<LocalityInterface[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		return this.http.get<any[]>(this.getAllLocalitysURL, {
			headers: headers,
		});
	}

	getAvailableUsers(): Observable<enduserInterface[]> {
		return this.http.get<JSON[]>(this.getAvailableUsersUrl, { headers: this.headers }).pipe(
			map((endusersRes: any) => {
				const newEnduserArray: enduserInterface[] = [];

				endusersRes.forEach((enduser: any) => {
					const newEnduser: enduserInterface = {
						id: enduser.id,
						fullName: enduser.full_name,
					};

					newEnduserArray.push(newEnduser);
				});

				return newEnduserArray;
			})
		);
	}

	getPendingLendings(location?: string, responsible?: string, request?: string): Observable<stockInterface[]> {
		let querystring = '';

		if (location != '') {
			querystring += '&idLocality=' + location;
		}

		if (responsible != '') {
			querystring += '&idResponsible=' + responsible;
		}

		if (request != '') {
			querystring += '&request=' + request;
		}

		const submitgetPendingLendingUrl = this.getPendingLendingUrl.replace('{querystring}', querystring);
		return this.http.get(submitgetPendingLendingUrl, { headers: this.headers }).pipe(
			map((stockRes: any[]) => {
				const newStockArray: stockInterface[] = [];

				for (const stock of stockRes) {
					const newStock: stockInterface = {
						id: stock.idStock,
						in: Number.parseInt(stock.in),
						out: Number.parseInt(stock.out),
						available: Number.parseInt(stock.qtd),
						idUserResponsible: stock.idResponsible,
						userResponsible: stock.responsible,
						idTypeMovement: '4',
						typeMovement: 'Return',
						typeMovementLabel: 'lbl_geral_retorno',
						expectedReturnDate: stock.expectedReturnDate,
						idMovement: stock.id,
						document: '',
						request: stock.request == '' ? 'N/A' : stock.request,
						observation: '',
						icon: 'faDolly',
						identification: stock.identification,
						relatedCi: {
							identification: stock.identification,
							id: stock.cmdb_id,
							cmdb_id: stock.cmdb_id,
							type: 'Consumível',
							location: stock.locality,
						},
					};

					newStockArray.push(newStock);
				}

				return newStockArray;
			})
		);
	}

	searchStockItemLogs(idCi: string): Observable<stockInterface[]> {
		const qryString = `cmdb_id=${idCi}`;
		const getStockLogsUrl = this.getStockLogsUrl.replace('{qryString}', qryString);
		return this.CmdbRegisterPageService.getCiData(idCi, new ShkDate()).pipe(
			switchMap((ciData) => {
				return this.http.get(getStockLogsUrl, { headers: this.headers }).pipe(
					map((stocks: any[]) => {
						const newStockArray: stockInterface[] = [];

						for (const stock of stocks) {
							const newStock: stockInterface = {
								id: stock.id,
								in: Number.parseInt(stock.in),
								out: Number.parseInt(stock.out),
								available: Number.parseInt(stock.qtd),
								idUserResponsible: stock.idResponsible,
								userResponsible: stock.responsible ? stock.responsible : '',
								idTypeMovement: stock.idTypeMovement,
								typeMovement: stock.typeMovement,
								typeMovementLabel: stock.labelTypeMovement ? stock.labelTypeMovement : '',
								createdAt: this.DateService.formatDate(stock.creationDate),
								movementDate: this.DateService.formatDate(stock.movementDate),
								expectedReturnDate: this.DateService.formatDate(stock.expectedReturnDate),
								document: stock.document,
								request: stock.request,
								observation: stock.observation,
								relatedCi: null,
								identification: null,
							};

							const consumable = ciData.isConsumable.toString();

							const newCi: ciInterface['ciArray'][number] = {
								identification: ciData.identification,
								cmdb_id: ciData.id,
								id: ciData.id,
								idLocation: Number.parseInt(ciData.id_location),
								location: stock.locality,
								type: 'Mold',
								//"isConsumable": ciData.isConsumable
								isConsumable: consumable === 'FALSE' || consumable === 'false' ? false : true,
							};

							newStock.relatedCi = newCi;

							newStockArray.push(newStock);
						}

						return newStockArray;
					})
				);
			})
		);
	}

	searchStockItem(idCi: string): Observable<stockInterface> {
		const qryString = `cmdb_id=${idCi}`;
		const getStockUrl = this.getStockUrl.replace('{qryString}', qryString);
		return this.CmdbRegisterPageService.getCiData(idCi, new ShkDate()).pipe(
			switchMap((ciData) => {
				return this.http.get(getStockUrl, { headers: this.headers }).pipe(
					map((stock: any) => {
						const newStock: stockInterface = {
							id: stock.cmdb_stock_id,
							available: stock.qtd == '' ? 0 : stock.qtd,
							out: stock.out,
							in: stock.in,
							document: '',
							request: '',
							observation: '',
							idUserResponsible: '',
							userResponsible: '',
							movementDate: stock.movementDate == '' ? 'N/A' : this.DateService.formatDate(stock.movementDate),
							createdAt: stock.createdAt == '' ? 'N/A' : this.DateService.formatDate(stock.createdAt),
							relatedCi: null,
							identification: null,
						};

						const newCi: ciInterface['ciArray'][number] = {
							identification: ciData.identification,
							cmdb_id: ciData.id,
							id: ciData.id,
							idLocation: Number.parseInt(ciData.id_location),
							location: ciData.location,
							type: ciData.type,
						};

						newStock.relatedCi = newCi;

						return newStock;
					})
				);
			})
		);
	}

	updateStock(stockData: stockInterface): Observable<any> {
		//let typeEnum = ['In', 'Out', 'Lend', 'Return'];
		const body = {
			cmdb_id: stockData.relatedCi.cmdb_id,
			locality: stockData.relatedCi.idLocation,
			in: stockData.in,
			out: stockData.out,
			identification: stockData.relatedCi.identification,
			type: stockData.idTypeMovement,
			responsible: stockData.idUserResponsible,
			movementDate: stockData.movementDate ? stockData.movementDate : new Date(),
			document: stockData.document,
			request: stockData.request,
			observation: stockData.observation,
			expectedReturnDate: stockData.expectedReturnDate ? stockData.expectedReturnDate : '',
			idMovement: stockData.idMovement,
			returnDate: stockData.returnDate ? stockData.returnDate : '',
			isConsumable: stockData.relatedCi.isConsumable,
		};

		return this.http.post(this.postStockUrl, body, { headers: this.headers });
	}

	formatReadableToDate(date: string): Date {
		return <any>date instanceof Date && !isNaN(<any>date)
			? new Date(date)
			: new Date(`${date.split('/')[2]}-${date.split('/')[1]}-${Number.parseInt(date.split('/')[0]) + 1}`);
	}

	getUserLending(idEnduser: string): Observable<ciRelationshipInterface[]> {
		const getUserLendingUrl = this.getUserLendingUrl.replace('{idEnduser}', idEnduser);
		return this.http.get<any[]>(getUserLendingUrl, { headers: this.headers }).pipe(
			map((userLendingRes) => {
				const newUserLendingArray: ciRelationshipInterface[] = [];
				for (const userLending of userLendingRes) {
					const newUserLending: ciRelationshipInterface = {
						cmdbId: userLending.cmdbId,
						name: userLending.nameCi,
						relationId: userLending.idPerantOrChild,
						relationshipId: userLending.relationId,
						relationType: userLending.relationType,
						expectedReturnDate: userLending.expectedReturnDate ? userLending.expectedReturnDate : 'N/A',
						returnDate: userLending.returnDate ? userLending.returnDate : 'N/A',
					};

					newUserLendingArray.push(newUserLending);
				}

				return newUserLendingArray;
			})
		);
	}
}
